import React from 'react';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import * as Sentry from '@sentry/react';

import { UFOExperienceContext } from '@/common/ufo';

interface FallbackProps {
  error: Error;
  resetError: any /* FIXME: Maybe get this type from the Sentry React SDK */;
}

const Fallback: React.FC<FallbackProps> = ({ error, resetError }) => (
  <SectionMessage
    actions={[
      <SectionMessageAction key="try-again" onClick={resetError}>
        Try again
      </SectionMessageAction>,
    ]}
    appearance="error"
    title="Something went wrong"
  >
    <p>Sorry, there was a problem loading this page.</p>
    {error ? (
      <pre>
        <code>{error.toString()}</code>
      </pre>
    ) : null}
  </SectionMessage>
);

interface ExperienceErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }: ErrorBoundaryProps) => {
  const ufoExperience = React.useContext(UFOExperienceContext);

  return (
    <Sentry.ErrorBoundary
      beforeCapture={scope => {
        scope.setTag('ufo-experience', ufoExperience.id);
        scope.setTag('error-type', 'error-boundary');
      }}
      onError={() => {
        // noinspection JSIgnoredPromiseFromCall
        ufoExperience.failure({ force: true });
      }}
      fallback={({ error, resetError }) => <Fallback error={error} resetError={resetError} />}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

const ExperienceErrorBoundary: React.FC<ExperienceErrorBoundaryProps> = ({ children }) => (
  <ErrorBoundary>{children}</ErrorBoundary>
);

export { ExperienceErrorBoundary };
export default ErrorBoundary;
